// ─────────────────────────────────────────────────────────────────────────────
// Site search index — a flat list of page titles (and key sections) used by the
// header search bar. Typing matches against title + keywords and returns the
// best entries so the user can jump straight to a page (a "quick path").
//
// Keep this list in sync with the routes in App.js. `hash` entries scroll to a
// section on the target page (e.g. the FAQ on the Help Center).
// ─────────────────────────────────────────────────────────────────────────────

export interface SearchEntry {
    title: string;
    to: string;
    /** Short context shown under the title in the suggestion list. */
    hint?: string;
    /** Extra terms (incl. English) that should also match this entry. */
    keywords?: string[];
}

export const PAGE_INDEX: SearchEntry[] = [
    { title: 'Events', to: '/events', hint: 'Veranstaltungen entdecken', keywords: ['veranstaltung', 'konzert', 'discover'] },
    { title: 'Profil', to: '/profile', hint: 'Ihr Profil', keywords: ['account', 'konto', 'profile'] },
    { title: 'Tickets', to: '/tickets', hint: 'Tickets & Bestellungen', keywords: ['ticket', 'bestellung', 'order', 'kauf'] },
    { title: 'Hilfe-Center', to: '/hilfe', hint: 'Antworten & Support', keywords: ['help', 'faq', 'support', 'fragen'] },
    { title: 'Anmelden', to: '/login', hint: 'Mit E-Mail oder Google anmelden', keywords: ['login', 'sign in', 'einloggen', 'registrieren'] },
    { title: 'TheBowlingClub', to: '/thebowlingclub', hint: 'Der Bowlingspieler des Monats – Turnier & Sponsoring', keywords: ['bowling', 'bowlingclub', 'turnier', 'tournament', 'sponsor', 'sponsoring', 'braunschweig'] },
    { title: 'Wir die Braunschweiger', to: '/we-the-people/wir-die-braunschweiger', hint: 'We The People – echte Treffen in Braunschweig', keywords: ['we the people', 'braunschweiger', 'braunschweig', 'offline', 'dating', 'after work', 'party', 'konferenz', 'netzwerken', 'community'] },

    // Help Center sections
    { title: 'Events veranstalten', to: '/hilfe#events-erstellen', hint: 'Private & öffentliche Events', keywords: ['event erstellen', 'create event', 'veranstalter', 'recurring', 'event pool', 'standard'] },
    { title: 'Häufige Fragen', to: '/hilfe#faq', hint: 'FAQ', keywords: ['faq', 'fragen', 'questions'] },
    { title: 'Tickets kaufen', to: '/hilfe#faq', hint: 'Kauf, Sitzplatz, Zahlung', keywords: ['buy ticket', 'kaufen', 'bezahlen'] },
    { title: 'Weiterverkauf & Rückerstattung', to: '/hilfe#faq', hint: 'Resale & Refund', keywords: ['resale', 'refund', 'rückerstattung', 'weiterverkauf'] },

    // Account self-service
    { title: 'Profil melden', to: '/profil-melden', hint: 'Nutzer oder Event melden', keywords: ['report', 'melden', 'meldung', 'missbrauch', 'abuse'] },
    { title: 'Profil löschen', to: '/profil-loeschen', hint: 'Konto löschen', keywords: ['delete account', 'konto löschen', 'account löschen', 'löschung'] },

    // Legal
    { title: 'Datenschutzerklärung', to: '/datenschutz', hint: 'DSGVO & Ihre Rechte', keywords: ['privacy', 'datenschutz', 'dsgvo', 'gdpr'] },
    { title: 'Nutzungsbedingungen', to: '/nutzungsbedingungen', hint: 'Regeln & verbotene Inhalte', keywords: ['terms', 'terms of use', 'agb', 'bedingungen', 'regeln', 'verboten', 'prohibited'] },
    { title: 'Impressum', to: '/impressum', hint: 'Anbieterkennzeichnung', keywords: ['imprint', 'legal'] },
    { title: 'Cookie-Richtlinie', to: '/cookies', hint: 'Cookies & Tracking', keywords: ['cookie', 'tracking', 'consent'] },
];

/** Normalise for accent/﻿case-insensitive matching. */
function norm(s: string): string {
    return s
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '');
}

/**
 * Rank PAGE_INDEX entries against a query. Title-prefix matches rank highest,
 * then title-substring, then keyword/hint matches. Returns up to `limit` hits.
 */
export function searchPages(query: string, limit = 6): SearchEntry[] {
    const q = norm(query.trim());
    if (!q) return [];

    const scored: { entry: SearchEntry; score: number }[] = [];

    for (const entry of PAGE_INDEX) {
        const title = norm(entry.title);
        const hay = norm([entry.title, entry.hint ?? '', ...(entry.keywords ?? [])].join(' '));

        let score = 0;
        if (title === q) score = 100;
        else if (title.startsWith(q)) score = 80;
        else if (title.includes(q)) score = 60;
        else if (hay.includes(q)) score = 40;

        if (score > 0) scored.push({ entry, score });
    }

    scored.sort((a, b) => b.score - a.score || a.entry.title.localeCompare(b.entry.title));
    return scored.slice(0, limit).map((s) => s.entry);
}
